/**
 * CortexHealth — S62 Validation Dashboard
 * Surfaces 4 health blocks (Persona, Group Config, Skills, Memory) by counting
 * `cortex_events` of type `validation.audit`. Adds an Audit tab with filters
 * by group, skill executed, and time window (24h / 7d).
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, AlertTriangle, CheckCircle2, Filter, Activity, Circle, Flag, RefreshCw, Brain, ShieldCheck } from "lucide-react";
import TopicHygiene from "@/components/cortex/TopicHygiene";
import CortexModulationProvenance from "@/components/cortex/CortexModulationProvenance";
import { Button } from "@/components/ui/button";
import { useCortexNodeMap, type NodeStatus } from "@/hooks/useCortexNodeMap";
import {
  useFlagsRegistry,
  FAMILY_LABELS,
  FAMILY_ORDER,
  statusOf,
  type FlagStatus,
} from "@/hooks/useFlagsRegistry";
import { useTomLive } from "@/hooks/useTomLive";
import { FreshnessIndicator } from "@/components/states/FreshnessIndicator";

// MS-07 — opções de auto-refresh para a aba ToM Live.
const TOM_REFRESH_OPTIONS: { label: string; value: number }[] = [
  { label: "Desligado", value: 0 },
  { label: "15s", value: 15_000 },
  { label: "30s", value: 30_000 },
  { label: "1min", value: 60_000 },
  { label: "5min", value: 300_000 },
];
const TOM_REFRESH_DEFAULT = 30_000;
const TOM_REFRESH_STORAGE_KEY = "cortex-health.tom.refresh-ms";

type Code = "persona_missing" | "typology_unknown" | "skill_stub" | "skill_failed" | "memory_search_broken";

interface Block { keys: Code[]; title: string; description: string; }
const BLOCKS: Block[] = [
  { keys: ["persona_missing"], title: "Persona", description: "Grupos sem agente/system_prompt — Cortex silencia o turno." },
  { keys: ["typology_unknown"], title: "Configuração de grupo", description: "Tipologias não reconhecidas pelo response-policy." },
  { keys: ["skill_stub", "skill_failed"], title: "Skills (Cortex OS)", description: "Falhas/stubs na execução inline de skills." },
  { keys: ["memory_search_broken"], title: "Memória semântica", description: "match_memories com erro ou sem fragmentos esperados." },
];

interface EventRow {
  group_id: string | null;
  occurred_at: string;
  payload: { code?: string; skill?: string; skill_name?: string; [k: string]: unknown } | null;
}

type Window = "24h" | "7d";

export default function CortexHealth() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const activeTab =
    tabParam === "nodes" || tabParam === "audit" || tabParam === "flags" || tabParam === "tom" || tabParam === "hygiene"
      ? (tabParam as "nodes" | "audit" | "flags" | "tom" | "hygiene")
      : "overview";
  const [loading, setLoading] = useState(true);
  const [windowSel, setWindowSel] = useState<Window>("24h");
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [recent, setRecent] = useState<EventRow[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Audit filters
  const [groupFilter, setGroupFilter] = useState<string>("");
  const [skillFilter, setSkillFilter] = useState<string>("all");
  const [codeFilter, setCodeFilter] = useState<string>("all");

  // Node Map (MS-01)
  const nodeWindowHours = windowSel === "24h" ? 24 : 24 * 7;
  const { rows: nodeRows, loading: nodesLoading, error: nodesError } = useCortexNodeMap(nodeWindowHours);

  // Flags Registry (MS-05) — read-only
  const flags = useFlagsRegistry();

  // ToM Live (MS-07) — read-only, auto-refresh + cache TTL
  const [tomIntervalMs, setTomIntervalMs] = useState<number>(() => {
    if (typeof window === "undefined") return TOM_REFRESH_DEFAULT;
    const raw = window.localStorage.getItem(TOM_REFRESH_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && TOM_REFRESH_OPTIONS.some((o) => o.value === n) ? n : TOM_REFRESH_DEFAULT;
  });
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(TOM_REFRESH_STORAGE_KEY, String(tomIntervalMs));
    }
  }, [tomIntervalMs]);
  const tom = useTomLive(tomIntervalMs);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const hours = windowSel === "24h" ? 24 : 24 * 7;
        const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        const { data, error: err } = await supabase
          .from("cortex_events")
          .select("group_id, occurred_at, payload")
          .eq("event_type", "validation.audit")
          .gte("occurred_at", since)
          .order("occurred_at", { ascending: false })
          .limit(1000);
        if (err) throw err;
        if (cancelled) return;
        const c: Record<string, number> = {};
        const rows = (data ?? []) as unknown as EventRow[];
        for (const r of rows) {
          const code = String(r.payload?.code ?? "unknown");
          c[code] = (c[code] ?? 0) + 1;
        }
        setCounts(c);
        setRecent(rows);
      } catch (e: unknown) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [windowSel]);

  const totalFor = (keys: Code[]): number => keys.reduce((a, k) => a + (counts[k] ?? 0), 0);

  const skillOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of recent) {
      const s = (r.payload?.skill ?? r.payload?.skill_name) as string | undefined;
      if (s) set.add(s);
    }
    return Array.from(set).sort();
  }, [recent]);

  const codeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of recent) if (r.payload?.code) set.add(String(r.payload.code));
    return Array.from(set).sort();
  }, [recent]);

  const filtered = useMemo(() => {
    return recent.filter((r) => {
      if (groupFilter && !(r.group_id ?? "").toLowerCase().includes(groupFilter.toLowerCase())) return false;
      if (codeFilter !== "all" && r.payload?.code !== codeFilter) return false;
      if (skillFilter !== "all") {
        const s = (r.payload?.skill ?? r.payload?.skill_name) as string | undefined;
        if (s !== skillFilter) return false;
      }
      return true;
    });
  }, [recent, groupFilter, skillFilter, codeFilter]);

  const filteredCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of filtered) {
      const code = String(r.payload?.code ?? "unknown");
      c[code] = (c[code] ?? 0) + 1;
    }
    return c;
  }, [filtered]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">Cortex Health (S62)</h1>
          <p className="text-sm text-muted-foreground">
            Auditoria de Persona, Configuração de Grupo, Skills e Memória.
          </p>
        </div>
        <div className="w-40">
          <Label className="text-xs">Janela</Label>
          <Select value={windowSel} onValueChange={(v) => setWindowSel(v as Window)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Últimas 24h</SelectItem>
              <SelectItem value="7d">Últimos 7 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando violações…
        </div>
      )}
      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-sm text-destructive">Erro ao carregar: {error}</CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setSearchParams((sp) => { sp.set("tab", v); return sp; }, { replace: true })}>
        <TabsList>
          <TabsTrigger value="overview">Visão geral</TabsTrigger>
          <TabsTrigger value="nodes">Node Map</TabsTrigger>
          <TabsTrigger value="audit">Auditoria</TabsTrigger>
          <TabsTrigger value="flags">Flags V19</TabsTrigger>
          <TabsTrigger value="tom">ToM Live</TabsTrigger>
          <TabsTrigger value="modulation"><ShieldCheck className="h-3 w-3 mr-1" />Modulação</TabsTrigger>
          <TabsTrigger value="hygiene"><ShieldCheck className="h-3 w-3 mr-1" />Topic Hygiene</TabsTrigger>
        </TabsList>

        <TabsContent value="hygiene" className="pt-4">
          <TopicHygiene />
        </TabsContent>

        <TabsContent value="modulation" className="pt-4">
          <CortexModulationProvenance />
        </TabsContent>

        <TabsContent value="overview" className="space-y-4 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {BLOCKS.map((b) => {
              const total = totalFor(b.keys);
              const ok = total === 0;
              return (
                <Card key={b.title} className={ok ? "border-success/40" : "border-warning/60"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center justify-between text-base">
                      <span>{b.title}</span>
                      {ok ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-semibold">{total}</div>
                    <p className="text-xs text-muted-foreground mt-1">{b.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Eventos recentes</CardTitle></CardHeader>
            <CardContent>
              {recent.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem violações na janela selecionada. ✅</p>
              ) : (
                <ul className="space-y-2 max-h-96 overflow-auto">
                  {recent.slice(0, 50).map((r, i) => (
                    <li key={i} className="text-xs flex items-start gap-2 border-b border-border/50 pb-2">
                      <Badge variant="outline" className="shrink-0">{String(r.payload?.code ?? "—")}</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-muted-foreground truncate">
                          group: {r.group_id?.slice(0, 8) ?? "—"} · {new Date(r.occurred_at).toLocaleString("pt-BR")}
                        </div>
                        {r.payload && <div className="font-mono truncate">{JSON.stringify(r.payload)}</div>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="nodes" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" /> Cortex Node Map
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                12 nós canônicos do Cortex OS. Verde: GA + emitindo eventos. Amarelo: shadow/canary ou GA sem tráfego. Cinza: off / sem flag.
              </p>
            </CardHeader>
            <CardContent>
              {nodesLoading && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando nós…
                </div>
              )}
              {nodesError && (
                <p className="text-sm text-destructive">Erro: {nodesError}</p>
              )}
              {!nodesLoading && !nodesError && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {nodeRows.map((r) => {
                    const dotClass: Record<NodeStatus, string> = {
                      green: "text-success fill-success",
                      yellow: "text-warning fill-warning",
                      gray: "text-muted-foreground fill-muted-foreground",
                    };
                    const borderClass: Record<NodeStatus, string> = {
                      green: "border-success/40",
                      yellow: "border-warning/50",
                      gray: "border-border",
                    };
                    return (
                      <Card key={r.node.key} className={borderClass[r.status]}>
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center justify-between text-sm">
                            <span className="truncate">{r.node.label}</span>
                            <Circle className={`h-3 w-3 ${dotClass[r.status]}`} />
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <p className="text-xs text-muted-foreground line-clamp-2">{r.node.description}</p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px] font-mono">
                              {r.flagKey ?? "—"}
                            </Badge>
                            <Badge
                              variant={r.enabled ? "default" : "secondary"}
                              className="text-[10px] uppercase"
                            >
                              {r.enabled === null ? "no-flag" : r.enabled ? "on" : "off"}
                            </Badge>
                          </div>
                          <div className="flex items-center justify-between pt-1 text-xs">
                            <span className="text-muted-foreground">Eventos {windowSel}</span>
                            <span className="font-semibold tabular-nums">{r.events24h}</span>
                          </div>
                          <div className="text-[11px] text-muted-foreground">
                            Última: {r.lastEventAt ? new Date(r.lastEventAt).toLocaleString("pt-BR") : "—"}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Filter className="h-4 w-4" /> Filtros
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">group_id (contém)</Label>
                <Input
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  placeholder="cole um group_id ou parte"
                />
              </div>
              <div>
                <Label className="text-xs">Skill executada</Label>
                <Select value={skillFilter} onValueChange={setSkillFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as skills</SelectItem>
                    {skillOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Código de violação</Label>
                <Select value={codeFilter} onValueChange={setCodeFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os códigos</SelectItem>
                    {codeOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card>
              <CardContent className="p-3">
                <div className="text-xs text-muted-foreground">Total filtrado</div>
                <div className="text-xl font-semibold">{filtered.length}</div>
              </CardContent>
            </Card>
            {Object.entries(filteredCounts).slice(0, 4).map(([code, n]) => (
              <Card key={code}>
                <CardContent className="p-3">
                  <div className="text-xs text-muted-foreground truncate">{code}</div>
                  <div className="text-xl font-semibold">{n}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Eventos ({filtered.length})</CardTitle></CardHeader>
            <CardContent>
              {filtered.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhum evento corresponde aos filtros.</p>
              ) : (
                <ul className="space-y-2 max-h-[600px] overflow-auto">
                  {filtered.slice(0, 200).map((r, i) => {
                    const skill = (r.payload?.skill ?? r.payload?.skill_name) as string | undefined;
                    return (
                      <li key={i} className="text-xs flex items-start gap-2 border-b border-border/50 pb-2">
                        <Badge variant="outline" className="shrink-0">{String(r.payload?.code ?? "—")}</Badge>
                        {skill && <Badge variant="secondary" className="shrink-0">{skill}</Badge>}
                        <div className="flex-1 min-w-0">
                          <div className="text-muted-foreground truncate">
                            group: {r.group_id ?? "—"} · {new Date(r.occurred_at).toLocaleString("pt-BR")}
                          </div>
                          {r.payload && <div className="font-mono truncate">{JSON.stringify(r.payload)}</div>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="flags" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Flag className="h-4 w-4" /> Registry de feature flags
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Read-only. Estado atual de todas as flags em <code className="font-mono">cortex_feature_flags</code>,
                    agrupadas por família operacional. Para alterar, use o painel próprio (ex.: Canary control para composer).
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={flags.refetch} disabled={flags.loading}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${flags.loading ? "animate-spin" : ""}`} />
                  Recarregar
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {flags.loading && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando flags…
                </div>
              )}
              {flags.error && <p className="text-sm text-destructive">Erro: {flags.error}</p>}
              {!flags.loading && !flags.error && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <Card>
                    <CardContent className="p-3">
                      <div className="text-xs text-muted-foreground">Total</div>
                      <div className="text-xl font-semibold tabular-nums">{flags.totals.total}</div>
                    </CardContent>
                  </Card>
                  <Card className="border-success/40">
                    <CardContent className="p-3">
                      <div className="text-xs text-muted-foreground">On (100%)</div>
                      <div className="text-xl font-semibold text-success tabular-nums">{flags.totals.on}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3">
                      <div className="text-xs text-muted-foreground">Off</div>
                      <div className="text-xl font-semibold tabular-nums">{flags.totals.off}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3">
                      <div className="text-xs text-muted-foreground">Ativos</div>
                      <div className="text-xl font-semibold tabular-nums">{flags.totals.on}</div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </CardContent>
          </Card>

          {!flags.loading && !flags.error && FAMILY_ORDER.map((family) => {
            const list = flags.byFamily[family];
            if (!list || list.length === 0) return null;
            return (
              <Card key={family}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>{FAMILY_LABELS[family]}</span>
                    <Badge variant="outline" className="font-mono text-[10px]">{list.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {list.map((row) => {
                      const status: FlagStatus = statusOf(row);
                      const statusVariant: Record<FlagStatus, "default" | "secondary" | "outline" | "destructive"> = {
                        on: "default",
                        off: "secondary",
                      };
                      const statusLabel: Record<FlagStatus, string> = {
                        on: "On",
                        off: "Off",
                      };
                      return (
                        <li
                          key={row.flag_key}
                          className="flex items-start gap-3 border-b border-border/50 pb-2 last:border-b-0 last:pb-0"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <code className="font-mono text-xs font-semibold">{row.flag_key}</code>
                              <Badge variant={statusVariant[status]} className="text-[10px] uppercase">
                                {statusLabel[status]}
                              </Badge>
                            </div>
                            {row.description && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{row.description}</p>
                            )}
                            <p className="text-[10px] text-muted-foreground mt-1">
                              Atualizado {new Date(row.updated_at).toLocaleString("pt-BR")}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </TabsContent>

        <TabsContent value="tom" className="space-y-4 pt-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="h-4 w-4" /> Theory of Mind — Live
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Read-only. Inferências de turno, modelos de membros e contribuição A/B (with vs without ToM).
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <FreshnessIndicator updatedAt={tom.lastUpdatedAt} prefix="Atualizada há" />
                  <div className="w-32">
                    <Select
                      value={String(tomIntervalMs)}
                      onValueChange={(v) => setTomIntervalMs(Number(v))}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TOM_REFRESH_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={String(o.value)}>
                            Auto: {o.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => tom.refetch({ force: true })}
                    disabled={tom.loading}
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${tom.loading ? "animate-spin" : ""}`} />
                    Recarregar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {tom.loading && (
                <div className="flex items-center gap-2 text-muted-foreground text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Carregando ToM…
                </div>
              )}
              {tom.error && <p className="text-sm text-destructive">Erro: {tom.error}</p>}
              {!tom.loading && !tom.error && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {tom.volumes.map((v) => (
                    <Card key={v.table}>
                      <CardContent className="p-3">
                        <div className="text-[11px] text-muted-foreground font-mono truncate">{v.table}</div>
                        <div className="text-xl font-semibold tabular-nums">{v.last_24h}</div>
                        <div className="text-[10px] text-muted-foreground">
                          últimas 24h{v.total > 0 ? ` · total ${v.total}` : ""}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {!tom.loading && !tom.error && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Últimas inferências de turno</CardTitle>
                </CardHeader>
                <CardContent>
                  {tom.inferences.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sem inferências recentes.</p>
                  ) : (
                    <ul className="space-y-2 max-h-[480px] overflow-auto">
                      {tom.inferences.map((r) => (
                        <li key={r.id} className="text-xs border-b border-border/50 pb-2 last:border-b-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">
                              {r.primary_intent ?? "—"}
                            </Badge>
                            {r.urgency !== null && (
                              <Badge variant="secondary" className="text-[10px]">
                                urg {Number(r.urgency).toFixed(2)}
                              </Badge>
                            )}
                            {r.vulnerability !== null && (
                              <Badge variant="secondary" className="text-[10px]">
                                vul {Number(r.vulnerability).toFixed(2)}
                              </Badge>
                            )}
                            {r.confidence_parsing !== null && (
                              <Badge variant="secondary" className="text-[10px]">
                                conf {Number(r.confidence_parsing).toFixed(2)}
                              </Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground mt-1 truncate">
                            {r.member_jid?.slice(0, 18) ?? "—"} · grp {r.group_id?.slice(0, 8) ?? "—"} ·{" "}
                            {new Date(r.created_at).toLocaleString("pt-BR")}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Top membros modelados</CardTitle>
                </CardHeader>
                <CardContent>
                  {tom.models.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Sem modelos.</p>
                  ) : (
                    <ul className="space-y-2 max-h-[480px] overflow-auto">
                      {tom.models.map((m) => (
                        <li key={m.id} className="text-xs border-b border-border/50 pb-2 last:border-b-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold truncate">
                              {m.display_name || m.member_jid?.slice(0, 18) || "—"}
                            </span>
                            <Badge variant="secondary" className="text-[10px]">
                              obs {m.observation_count ?? 0}
                            </Badge>
                            {m.last_intent && (
                              <Badge variant="outline" className="text-[10px]">{m.last_intent}</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-wrap mt-1 text-[10px] text-muted-foreground">
                            {m.formality !== null && <span>form {Number(m.formality).toFixed(2)}</span>}
                            {m.verbosity !== null && <span>verb {Number(m.verbosity).toFixed(2)}</span>}
                            {m.directness !== null && <span>dir {Number(m.directness).toFixed(2)}</span>}
                            {m.assertiveness !== null && <span>ass {Number(m.assertiveness).toFixed(2)}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {!tom.loading && !tom.error && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Últimas contribuições A/B (with vs without ToM)</CardTitle>
              </CardHeader>
              <CardContent>
                {tom.contributions.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Sem runs de contribuição.</p>
                ) : (
                  <ul className="space-y-2 max-h-96 overflow-auto">
                    {tom.contributions.map((c) => {
                      const winnerVariant =
                        c.winner === "with_tom" ? "default" : c.winner === "without_tom" ? "destructive" : "outline";
                      return (
                        <li key={c.id} className="text-xs border-b border-border/50 pb-2 last:border-b-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={winnerVariant} className="text-[10px] uppercase">
                              {c.winner ?? "tie"}
                            </Badge>
                            <Badge variant="secondary" className="text-[10px]">
                              with {c.with_tom_score !== null ? Number(c.with_tom_score).toFixed(2) : "—"}
                            </Badge>
                            <Badge variant="secondary" className="text-[10px]">
                              without {c.without_tom_score !== null ? Number(c.without_tom_score).toFixed(2) : "—"}
                            </Badge>
                            {c.contribution_score !== null && (
                              <Badge variant="outline" className="text-[10px]">
                                Δ {Number(c.contribution_score).toFixed(2)}
                              </Badge>
                            )}
                            {c.judge_model && (
                              <Badge variant="outline" className="text-[10px] font-mono">{c.judge_model}</Badge>
                            )}
                          </div>
                          <div className="text-muted-foreground mt-1">
                            grp {c.group_id?.slice(0, 8) ?? "—"} · {new Date(c.created_at).toLocaleString("pt-BR")}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
